import { h } from 'preact';
import { useCallback, useEffect, useState } from 'preact/hooks';
import { memo } from 'preact/compat';

import { useSelector, useDispatch } from 'react-redux';

import { styled } from '@mui/material/styles';

import List from '@mui/material/List';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import ListSubheader from '@mui/material/ListSubheader';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';

import Refresh from '@mui/icons-material/Refresh.js';

import MuiDrawer from '@mui/material/Drawer';

import { actions } from '../../store/state/lruDrawer.js';
import { getLruCache } from '../../apis/kappa.js';
import globalErrorHandler from '../../utils/errorHandler.js';
import increase from '../../utils/increase.js';
import { fullDayFormatter } from '../../utils/dateFormatters.js';

const DRAWER_WIDTH = 480;

function getDrawerState(state) {
  return state.lruDrawer;
}

const Drawer = styled(
  MuiDrawer,
  {}
)(({ theme, open }) => ({
  '& .MuiDrawer-paper': {
    whiteSpace: 'nowrap',
    width: DRAWER_WIDTH,
    maxWidth: '100vw',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: 'border-box',
  },
  ...(!open && {
    overflowX: 'hidden',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(9),
    },
  }),
}));

function LruEntry(props) {
  const { model } = props;

  return (
    <ListItem>
      <ListItemText
        sx={{ flexGrow: 1 }}
        primary={
          <>
            <Typography
              sx={{ display: 'inline' }}
              component="span"
              variant="body1"
            >
              {model.key}:{' '}
            </Typography>
            <Typography
              sx={{ display: 'inline' }}
              component="span"
              variant="body2"
            >
              {model.value}
            </Typography>
          </>
        }
        secondary={
          <>
            <Typography
              sx={{ display: 'inline' }}
              component="span"
              variant="body2"
            >
              Last used{' '}
            </Typography>
            {model.lastUsed ? fullDayFormatter(model.lastUsed) : 'Never'}
          </>
        }
      />
    </ListItem>
  );
}

function renderLruEntry(lruEntry) {
  return <LruEntry key={lruEntry._id} model={lruEntry} />;
}

function LruSidebar(_props) {
  const dispatch = useDispatch();

  const [lruCache, setLruCache] = useState([]);
  const [updateCount, setUpdateCount] = useState(0);

  const open = useSelector(getDrawerState);

  const closeDrawer = useCallback(
    function closeDrawer() {
      dispatch(actions.CLOSE());
    },
    [dispatch]
  );

  useEffect(
    function fetchLru() {
      function onResponse(cache) {
        setLruCache(cache);
      }

      getLruCache().then(onResponse).catch(globalErrorHandler);
    },
    [updateCount]
  );

  const refresh = useCallback(function refresh() {
    setUpdateCount(increase);
  }, []);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={closeDrawer}
      ModalProps={{
        keepMounted: true,
      }}
    >
      <Box sx={{ px: 3, py: 2 }}>
        <List
          subheader={
            <Stack direction={'row'} alignItems={'center'}>
              <ListSubheader sx={{ px: 0, flexGrow: 1 }}>
                <Typography variant="body1">Entries in LRU Cache</Typography>
              </ListSubheader>
              <Button
                size="small"
                variant="outlined"
                sx={{ height: 32 }}
                onClick={refresh}
              >
                <Refresh />
              </Button>
            </Stack>
          }
        >
          {lruCache.map(renderLruEntry)}
        </List>
      </Box>
    </Drawer>
  );
}

export default memo(LruSidebar);
